import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HintedButton } from "@/components/HintedButton";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Field, Select, Textarea } from "@/components/ui/field";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { directoryQuery, nameFor } from "@/features/directory/queries";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import { approveSubmission, rejectSubmission, requestHandover } from "@/features/submissions/api";
import { QueueAge } from "@/features/submissions/components/QueueAge";
import { SubmissionDiff } from "@/features/submissions/components/SubmissionDiff";
import { reviewQuery } from "@/features/submissions/queries";

/**
 * Where a reviewer actually decides.
 *
 * **Every refusal on this page comes from the server.** `can_decide` and
 * `refusal` are computed by `mdm.workflow.review.review_context`, which
 * calls the same `refuse_decision` that `POST /approve` raises from — so
 * the sentence under a disabled Approve button is, by construction, the
 * sentence the server would answer with. Re-deriving it from `can()`
 * would be a second implementation of the authorisation rules, and the
 * first time they disagreed the button would lie.
 *
 * Rejection needs a comment, enforced here *and* by
 * `ck_submission_rejected_has_comment` in the database. The UI half is
 * not the guard; it is the part that says why the guard is there before
 * the user hits it.
 */
/**
 * The shared description for Approve and Reject.
 *
 * A module constant rather than `useId`, because two controls have to
 * agree on it and only one page ever renders it. Approve and Reject are
 * blocked by the same sentence whenever they are blocked together — the
 * one case where they differ (a cascade the reviewer cannot fully
 * approve but may still reject) leaves Reject enabled, so the paragraph
 * still describes exactly the buttons pointing at it.
 */
const REASON_ID = "decision-reason";

export const Route = createFileRoute("/_authed/submissions/$submissionId")({
  staticData: {
    crumbs: [{ key: "nav.submissions", to: "/submissions" }, { key: "nav.breadcrumb.submission" }],
  },
  loader: async ({ context, params }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await Promise.all([
      context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale)),
      context.queryClient.ensureQueryData(directoryQuery()),
      context.queryClient.ensureQueryData(reviewQuery(params.submissionId)),
    ]);
  },
  component: SubmissionReviewPage,
});

function SubmissionReviewPage() {
  const { t } = useTranslation();
  const { submissionId } = Route.useParams();
  const queryClient = useQueryClient();
  const { me, can } = useSession();
  const locale = me.resolved_locale;

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));
  const { data: directory } = useSuspenseQuery(directoryQuery());
  const { data: review } = useSuspenseQuery(reviewQuery(submissionId));
  const submission = review.submission;

  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const [handingOver, setHandingOver] = useState(false);
  const [handoverTo, setHandoverTo] = useState("");
  const [reason, setReason] = useState("");

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["submission-review", submissionId] });
    void queryClient.invalidateQueries({ queryKey: ["submissions"] });
    // The decision changed a schema version or a record, so anything
    // reading those is stale too.
    void queryClient.invalidateQueries({ queryKey: ["records"] });
    void queryClient.invalidateQueries({ queryKey: ["schema"] });
  };

  const approve = useMutation({
    mutationFn: () => approveSubmission(submissionId),
    onSuccess: () => {
      toast.success(t("submissions.approved"));
      invalidate();
    },
  });

  const reject = useMutation({
    mutationFn: () => rejectSubmission(submissionId, comment),
    onSuccess: () => {
      setRejecting(false);
      setComment("");
      toast.success(t("submissions.rejected"));
      invalidate();
    },
  });

  const handover = useMutation({
    mutationFn: () => requestHandover(submissionId, { to_reviewer_id: handoverTo, reason }),
    onSuccess: () => {
      setHandingOver(false);
      setHandoverTo("");
      setReason("");
      toast.success(t("submissions.handover.requested"));
      void queryClient.invalidateQueries({ queryKey: ["handovers"] });
    },
  });

  const type = index.byId.get(submission.master_type_id);
  const typeName =
    type === undefined
      ? submission.master_type_id
      : (type.display_name[locale] ?? type.display_name[me.tenant.default_locale] ?? type.code);

  // The server already said what can be done and why not; the only thing
  // left for the client to decide is the wording.
  //
  // Two answers, not one. Rejecting promotes nothing, so a reviewer
  // missing an approve grant on one type of a cascade may still reject
  // it — `can_reject` is how the API says so, and collapsing the two
  // would grey out a button the server would honour.
  const blocked =
    review.refusal === null
      ? undefined
      : t(`submissions.refusal.${review.refusal}`, {
          defaultValue: review.refusal_detail ?? t("submissions.refusal.forbidden"),
        });

  const approveHint = review.can_decide
    ? undefined
    : (blocked ??
      t("submissions.refusal.insufficientCoverage", {
        types: review.missing_master_types.map((id) => index.byId.get(id)?.code ?? id).join(", "),
      }));
  const rejectHint = review.can_reject ? undefined : blocked;

  // A handover moves an in-flight submission to somebody else, so it only
  // makes sense while it is still in flight.
  const handoverHint =
    submission.status !== "pending"
      ? t("submissions.handover.notPending")
      : review.refusal === "not-assigned-reviewer"
        ? t("submissions.handover.notYours")
        : review.refusal === "forbidden"
          ? t("submissions.refusal.forbidden")
          : undefined;

  const candidates = directory.all.filter(
    (user) => user.is_active && user.id !== submission.submitted_by && user.id !== me.user_id,
  );

  return (
    <>
      <PageHeader
        title={t("submissions.review.title", { type: typeName })}
        description={t(`submissions.action.${submission.action}`)}
      />

      {/* Its own bar rather than `PageHeader`'s `actions` slot. That slot
          is `shrink-0`, so a control carrying a sentence of explanation
          squeezes the `<h1>` down to nothing and the two overlap — which
          is exactly what shipped the first time. Prose next to a title
          needs its own row. */}
      <section
        aria-label={t("submissions.decide")}
        className="mb-6 flex flex-col gap-2 rounded-panel border border-border bg-surface p-3 shadow-panel"
      >
        <div className="flex flex-wrap gap-2">
          <HintedButton
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
            hintId={approveHint === undefined ? undefined : REASON_ID}
            tooltip={approveHint === undefined ? t("tooltips.approve") : undefined}
          >
            {t("submissions.approve")}
          </HintedButton>
          <HintedButton
            variant="danger"
            onClick={() => setRejecting(true)}
            hintId={rejectHint === undefined ? undefined : REASON_ID}
            tooltip={rejectHint === undefined ? t("tooltips.reject") : undefined}
          >
            {t("submissions.reject")}
          </HintedButton>
          {can("handover.request") && (
            <HintedButton
              variant="secondary"
              onClick={() => setHandingOver(true)}
              hint={handoverHint}
              tooltip={handoverHint === undefined ? t("tooltips.requestHandover") : undefined}
            >
              {t("submissions.handover.request")}
            </HintedButton>
          )}
        </div>

        {/* One sentence for the group. Both buttons point at it, which is
            ordinary ARIA and half the height of printing it twice. */}
        {(approveHint ?? rejectHint) !== undefined && (
          <p id={REASON_ID} className="max-w-prose text-xs text-muted">
            {approveHint ?? rejectHint}
          </p>
        )}
      </section>

      <dl className="mb-6 grid gap-x-6 gap-y-2 rounded-panel border border-border bg-surface p-4 text-sm shadow-panel sm:grid-cols-2 lg:grid-cols-4">
        <Detail label={t("submissions.columns.status")}>
          <StatusBadge
            status={
              submission.status as "draft" | "pending" | "approved" | "rejected" | "cancelled"
            }
          />
        </Detail>
        <Detail label={t("submissions.columns.submittedBy")}>
          {nameFor(directory, submission.submitted_by) ?? "—"}
        </Detail>
        <Detail label={t("submissions.columns.reviewer")}>
          {submission.reviewer_id === null
            ? t("submissions.unassigned")
            : (nameFor(directory, submission.reviewer_id) ?? "—")}
        </Detail>
        <Detail label={t("submissions.columns.waiting")}>
          <QueueAge submittedAt={submission.submitted_at} />
        </Detail>
      </dl>

      {submission.decision_comment !== null && (
        <div className="mb-6 rounded-panel border border-border bg-surface-muted p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t("submissions.decisionComment")}
          </h2>
          <p className="mt-1 text-sm text-foreground">{submission.decision_comment}</p>
          <p className="mt-1 text-xs text-muted">
            {nameFor(directory, submission.decided_by) ?? "—"}
          </p>
        </div>
      )}

      {/* A cascade's own headline. DEE-30 asks for the affected subtree to
          be visible *before* approval, and the count is what makes the
          scope obvious at a glance — "1 record" and "1 record and 47
          under it" are different decisions. */}
      {review.items.length > 1 && (
        <p className="mb-3 rounded-panel border border-warning bg-warning-surface p-3 text-sm text-foreground">
          {t("submissions.cascadeNotice", { count: review.items.length })}
        </p>
      )}

      <h2 className="mb-2 text-sm font-semibold text-foreground">{t("submissions.changes")}</h2>
      <SubmissionDiff items={review.items} />

      {review.missing_master_types.length > 0 && (
        // Approval is blocked; rejection is not. The panel says which,
        // rather than leaving the reviewer to infer it from two buttons
        // in different states.
        <p className="mt-4 rounded-panel border border-danger bg-danger-surface p-3 text-sm text-danger">
          {t("submissions.refusal.insufficientCoverage", {
            types: review.missing_master_types
              .map((id) => index.byId.get(id)?.code ?? id)
              .join(", "),
          })}
        </p>
      )}

      <p className="mt-6">
        {/* `inline-flex min-h-11` for the touch-target rule (DEE-85): a
            standalone navigational link is a control, not prose, and this
            one measured 120×17. */}
        <Link
          to="/submissions"
          className="inline-flex min-h-11 items-center text-sm text-primary hover:underline"
        >
          {t("submissions.backToQueue")}
        </Link>
      </p>

      <ConfirmDialog
        open={rejecting}
        onOpenChange={setRejecting}
        title={t("submissions.rejectDialog.title")}
        description={t("submissions.rejectDialog.body")}
        confirmLabel={t("submissions.reject")}
        destructive
        pending={reject.isPending}
        // Enforced here and by `ck_submission_rejected_has_comment`. The
        // UI's job is to say why before the user hits the constraint.
        confirmHint={
          comment.trim() === "" ? t("submissions.rejectDialog.commentRequired") : undefined
        }
        onConfirm={() => reject.mutate()}
      >
        <Field
          id="reject-comment"
          label={t("submissions.rejectDialog.comment")}
          required
          requiredLabel={t("common.required")}
          hint={t("submissions.rejectDialog.commentHint")}
        >
          {(aria) => (
            <Textarea
              {...aria}
              rows={4}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          )}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={handingOver}
        onOpenChange={setHandingOver}
        title={t("submissions.handover.title")}
        description={t("submissions.handover.body")}
        confirmLabel={t("submissions.handover.request")}
        pending={handover.isPending}
        confirmHint={
          handoverTo === "" || reason.trim() === ""
            ? t("submissions.handover.incomplete")
            : undefined
        }
        onConfirm={() => handover.mutate()}
      >
        <div className="space-y-3">
          <Field
            id="handover-to"
            label={t("submissions.handover.to")}
            required
            requiredLabel={t("common.required")}
            // The server refuses the submitter as a target — that is the
            // gap that would let a creator route their own work back to
            // themselves — so they are not offered.
            hint={t("submissions.handover.toHint")}
          >
            {(aria) => (
              <Select
                {...aria}
                tooltip={t("tooltips.handoverTo")}
                value={handoverTo}
                onChange={(event) => setHandoverTo(event.target.value)}
              >
                <option value="">{t("records.form.choose")}</option>
                {candidates.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name ?? user.email}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            id="handover-reason"
            label={t("submissions.handover.reason")}
            required
            requiredLabel={t("common.required")}
          >
            {(aria) => (
              <Textarea
                {...aria}
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>
        </div>
      </ConfirmDialog>
    </>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="min-w-0 truncate text-foreground">{children}</dd>
    </div>
  );
}
